/**
 * TanStack Query factories + mutation helpers for the provider portal.
 *
 * All RPCs in `20260609000100_phase4d_provider_rpcs.sql` are SECURITY DEFINER
 * with explicit `is_admin() / is_provider()` gates. RLS on the underlying
 * tables is the second line of defence for direct reads (e.g. order_lines,
 * patients via `provider_patient_view`).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface ProviderKpis {
  provider_id: string | null;
  awaiting_acceptance: number;
  in_production: number;
  quality_check: number;
  ready_to_ship: number;
  shipped_7d: number;
}

export interface ProviderActivityRow {
  history_id: string;
  order_id: string;
  order_number: string;
  order_line_id: string;
  patient_first_name: string | null;
  patient_short_code: string | null;
  from_status: string | null;
  to_status: string;
  changed_at: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
}

export type ProviderStatusFilter =
  | 'all'
  | 'pending_acceptance'
  | 'in_production'
  | 'quality_check'
  | 'ready_to_ship'
  | 'shipped';

export interface ProviderPipelineRow {
  order_line_id: string;
  order_id: string;
  order_number: string;
  line_type: string;
  product_name: string;
  patient_first_name: string | null;
  patient_short_code: string | null;
  status: string;
  assigned_at: string | null;
  expected_delivery_date: string | null;
  provider_observations: string | null;
}

export interface ProviderPipelineResult {
  rows: ProviderPipelineRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProviderPipelineParams {
  status: ProviderStatusFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ProviderLineDetail {
  order_line_id: string;
  order_id: string;
  order_number: string;
  line_type: string;
  status: string;
  configuration: Record<string, unknown>;
  modifications: Array<{
    region: string;
    modification_type: string;
    intensity?: 'light' | 'medium' | 'strong' | null;
    parameters?: Record<string, unknown>;
  }>;
  unit_price_aed: number;
  quantity: number;
  assigned_at: string | null;
  expected_delivery_date: string | null;
  provider_observations: string | null;
  product: { id: string; name_en: string; sku: string } | null;
  patient: {
    id: string;
    first_name: string;
    /** Last name is masked to a single initial for providers (e.g. "M."). */
    last_name: string;
    date_of_birth: string | null;
    gender: string | null;
  } | null;
  short_code: string | null;
  clinic: { id: string; name: string } | null;
  podiatrist: { id: string; full_name: string } | null;
}

export interface SuggestModificationInput {
  orderLineId: string;
  fieldPath: string;
  currentValue: unknown;
  proposedValue: unknown;
  rationale: string;
}

/* -------------------------------------------------------------------------- */
/*  Query keys                                                                */
/* -------------------------------------------------------------------------- */

export const providerKeys = {
  all: ['provider'] as const,
  kpis: (providerId: string | null | undefined) =>
    [...providerKeys.all, 'kpis', providerId ?? null] as const,
  activity: (providerId: string | null | undefined, limit: number) =>
    [...providerKeys.all, 'activity', providerId ?? null, limit] as const,
  pipeline: (params: ProviderPipelineParams) =>
    [...providerKeys.all, 'pipeline', params] as const,
  lineDetail: (lineId: string) => [...providerKeys.all, 'line', lineId] as const,
  lineFiles: (lineId: string) => [...providerKeys.all, 'line-files', lineId] as const,
};

/* -------------------------------------------------------------------------- */
/*  Queries                                                                   */
/* -------------------------------------------------------------------------- */

export function providerKpisQuery(
  client: SupabaseClient<Database>,
  providerId: string | null,
) {
  return {
    queryKey: providerKeys.kpis(providerId),
    queryFn: async (): Promise<ProviderKpis> => {
      const args: { p_provider_id?: string } = {};
      if (providerId) args.p_provider_id = providerId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client as any).rpc('provider_dashboard_kpis', args);
      if (error) throw error;
      const j = (data ?? {}) as Record<string, unknown>;
      return {
        provider_id: (j.provider_id ?? null) as string | null,
        awaiting_acceptance: Number(j.awaiting_acceptance ?? 0),
        in_production: Number(j.in_production ?? 0),
        quality_check: Number(j.quality_check ?? 0),
        ready_to_ship: Number(j.ready_to_ship ?? 0),
        shipped_7d: Number(j.shipped_7d ?? 0),
      };
    },
  };
}

export function providerActivityQuery(
  client: SupabaseClient<Database>,
  providerId: string | null,
  limit = 10,
) {
  return {
    queryKey: providerKeys.activity(providerId, limit),
    queryFn: async (): Promise<ProviderActivityRow[]> => {
      const args: { p_provider_id?: string; p_limit?: number } = { p_limit: limit };
      if (providerId) args.p_provider_id = providerId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client as any).rpc('provider_recent_activity', args);
      if (error) throw error;
      return (data ?? []) as ProviderActivityRow[];
    },
  };
}

export function providerPipelineQuery(
  client: SupabaseClient<Database>,
  params: ProviderPipelineParams,
) {
  return {
    queryKey: providerKeys.pipeline(params),
    queryFn: async (): Promise<ProviderPipelineResult> => {
      const pageSize = Math.max(1, Math.min(params.pageSize ?? 25, 200));
      const page = Math.max(0, params.page ?? 0);
      const args: {
        p_status: ProviderStatusFilter;
        p_search?: string;
        p_limit: number;
        p_offset: number;
      } = {
        p_status: params.status,
        p_limit: pageSize,
        p_offset: page * pageSize,
      };
      if (params.search && params.search.trim()) args.p_search = params.search.trim();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client as any).rpc('provider_pipeline_lines', args);
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const total = rows.length > 0 ? Number(rows[0]!.total_count ?? rows.length) : 0;
      return {
        rows: rows.map((r) => ({
          order_line_id: String(r.order_line_id),
          order_id: String(r.order_id ?? ''),
          order_number: String(r.order_number ?? ''),
          line_type: String(r.line_type ?? ''),
          product_name: String(r.product_name ?? ''),
          patient_first_name: (r.patient_first_name ?? null) as string | null,
          patient_short_code: (r.patient_short_code ?? null) as string | null,
          status: String(r.status ?? ''),
          assigned_at: (r.assigned_at ?? null) as string | null,
          expected_delivery_date: (r.expected_delivery_date ?? null) as string | null,
          provider_observations: (r.provider_observations ?? null) as string | null,
        })),
        total,
        page,
        pageSize,
      };
    },
  };
}

export function providerLineDetailQuery(
  client: SupabaseClient<Database>,
  orderLineId: string,
) {
  return {
    queryKey: providerKeys.lineDetail(orderLineId),
    queryFn: async (): Promise<ProviderLineDetail | null> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client as any).rpc('provider_line_detail', {
        p_order_line_id: orderLineId,
      });
      if (error) throw error;
      if (!data) return null;
      const j = data as Record<string, unknown>;
      return {
        order_line_id: String(j.order_line_id),
        order_id: String(j.order_id ?? ''),
        order_number: String(j.order_number ?? ''),
        line_type: String(j.line_type ?? ''),
        status: String(j.status ?? ''),
        configuration: (j.configuration ?? {}) as Record<string, unknown>,
        modifications: Array.isArray(j.modifications)
          ? (j.modifications as ProviderLineDetail['modifications'])
          : [],
        unit_price_aed: Number(j.unit_price_aed ?? 0),
        quantity: Number(j.quantity ?? 1),
        assigned_at: (j.assigned_at ?? null) as string | null,
        expected_delivery_date: (j.expected_delivery_date ?? null) as string | null,
        provider_observations: (j.provider_observations ?? null) as string | null,
        product: (j.product ?? null) as ProviderLineDetail['product'],
        patient: (j.patient ?? null) as ProviderLineDetail['patient'],
        short_code: (j.short_code ?? null) as string | null,
        clinic: (j.clinic ?? null) as ProviderLineDetail['clinic'],
        podiatrist: (j.podiatrist ?? null) as ProviderLineDetail['podiatrist'],
      };
    },
  };
}

export interface ProviderLineFile {
  id: string;
  order_line_id: string;
  file_kind: string;
  storage_bucket: string;
  storage_path: string;
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  uploaded_at: string;
}

export function providerLineFilesQuery(
  client: SupabaseClient<Database>,
  orderLineId: string,
) {
  return {
    queryKey: providerKeys.lineFiles(orderLineId),
    queryFn: async (): Promise<ProviderLineFile[]> => {
      const { data, error } = await client
        .from('order_line_files')
        .select(
          'id, order_line_id, file_kind, storage_bucket, storage_path, file_name,' +
            ' file_size_bytes, mime_type, uploaded_at',
        )
        .eq('order_line_id', orderLineId)
        .eq('is_archived', false)
        .order('uploaded_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProviderLineFile[];
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Mutations                                                                 */
/* -------------------------------------------------------------------------- */

export async function providerTransitionLineStatus(
  client: SupabaseClient<Database>,
  orderLineId: string,
  toStatus: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client as any).rpc('provider_transition_line_status', {
    p_order_line_id: orderLineId,
    p_target_status: toStatus,
  });
  if (error) throw error;
}

export async function providerUpdateObservations(
  client: SupabaseClient<Database>,
  orderLineId: string,
  text: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client as any).rpc('provider_update_observations', {
    p_order_line_id: orderLineId,
    p_text: text,
  });
  if (error) throw error;
}

export async function providerSuggestModification(
  client: SupabaseClient<Database>,
  input: SuggestModificationInput,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client as any).rpc('provider_suggest_modification', {
    p_order_line_id: input.orderLineId,
    p_field_path: input.fieldPath,
    p_current_value: input.currentValue ?? null,
    p_proposed_value: input.proposedValue,
    p_rationale: input.rationale,
  });
  if (error) throw error;
  return String(data ?? '');
}

/**
 * Upload a finished-product photo for a line. Returns the inserted file row.
 * Storage path follows the brief: `clinic/{clinic_id}/orders/{order_id}/{order_line_id}/finished/{filename}`
 */
export interface UploadFinishedPhotoArgs {
  orderLineId: string;
  orderId: string;
  clinicId: string;
  file: File;
  uploadedBy: string;
}

export async function uploadFinishedPhoto(
  client: SupabaseClient<Database>,
  args: UploadFinishedPhotoArgs,
): Promise<{ path: string; id: string }> {
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
  const path = [
    'clinic',
    args.clinicId,
    'orders',
    args.orderId,
    args.orderLineId,
    'finished',
    `${Date.now()}_${safeName}`,
  ].join('/');

  // We reuse the existing `scans` bucket because finished-product photos are
  // operational artefacts and the scans bucket already grants insert to
  // assigned providers via RLS. (See 20260601000800_storage.sql.)
  const bucket = 'scans';
  const { error: uploadErr } = await client.storage.from(bucket).upload(path, args.file, {
    cacheControl: '3600',
    upsert: false,
    contentType: args.file.type || 'image/jpeg',
  });
  if (uploadErr) throw uploadErr;

  const insertPayload = {
    order_line_id: args.orderLineId,
    file_kind: 'finished_product',
    storage_bucket: bucket,
    storage_path: path,
    file_name: args.file.name,
    file_size_bytes: args.file.size,
    mime_type: args.file.type || 'image/jpeg',
    uploaded_by: args.uploadedBy,
  } as unknown as Database['public']['Tables']['order_line_files']['Insert'];

  const { data, error } = await client
    .from('order_line_files')
    .insert(insertPayload)
    .select('id')
    .single();
  if (error) throw error;
  return { path, id: String((data as { id: string }).id) };
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Resolve the provider_id for the currently signed-in user. Returns null when
 * the user is not linked to any provider (or is an admin/podiatrist viewing
 * the provider portal as a fallback).
 */
export async function resolveCurrentProviderId(
  client: SupabaseClient<Database>,
  userId: string,
): Promise<string | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client as any)
    .from('provider_user_links')
    .select('provider_id')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data as { provider_id?: string } | null)?.provider_id ?? null;
}
